import React, { useState, useEffect } from 'react';
import {
  Page,
  Form,
  Layout,
  FormLayout,
  DatePicker,
  Range,
  ChoiceList,
  Button,
 } from '@shopify/polaris';
 import {
  useField,
  useForm,
  SubmitResult,
} from '@shopify/react-form';
import { useLocation, useNavigate } from "react-router-dom";
import createApp from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge-utils";

import useMessage from '../../hooks/useMessage';
import { formSubmitHandler } from '../../utils/forms';
import { sendInstallationDataV2 } from '../../requests';
import { getRequestPayload, convertToV2Payload } from './utils';
import formConfig from './config';
import styles from './DataBackfill.module.css';
import { StoreType } from '../Settings/components/types';
import { getAppConfig, getShop } from '../../utils/misc';
import { handleError } from '../../utils/errorHandling';
import type { BackfillScope } from './constants';

const appConfig = getAppConfig();

interface PreviousStepFieldValues {
  iterableKey: string;
  email: string;
  storeType: StoreType;
}

function isPreviousStepFieldValues(value: unknown): value is PreviousStepFieldValues {
  return Boolean((value as PreviousStepFieldValues).iterableKey && (value as PreviousStepFieldValues).email);
}

function DataBackfill () {
  const navigate = useNavigate();
  const location = useLocation();
  const { bannerMarkup, showMessage } = useMessage();
  const [shop, setShop] = useState("");

  useEffect(() => {
    const fetchShop = async () => {
      try {
        if (appConfig) {
          const app = createApp(appConfig);
          const token = await getSessionToken(app)
            .then((token: string) => token)
            .catch((err: unknown) => {
              handleError(err, showMessage, navigate);
              return "";
            });

          const shopDomain = getShop(token);
          setShop(shopDomain);
        }
      } catch (err) {
        handleError(err, showMessage, navigate);
      }
    };

    fetchShop();
  }, [showMessage, navigate]);

  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date(),
  });

  const {
    fields,
    submit,
    submitting,
  } = useForm({
    fields: {
      timeFrom: useField({
        value: selectedDates,
        validates: [

        ],
      }),
      scopes: useField<(BackfillScope | 'hidden')[]>({
        value: ['hidden'],
        validates: [

        ],
      }),
    },
    async onSubmit(formValues): Promise<SubmitResult>  {
      const state = location?.state;

      if (state && isPreviousStepFieldValues(state)) {
        const payload = getRequestPayload({ ...formValues, ...state });
        
        // Use Scala v2 endpoint for all stores (both traditional and custom)
        if (!shop) {
          throw new Error("Shop not available");
        }
        
        const v2Config = {
          request: (payload: any) => {
            const v2Payload = convertToV2Payload(payload);
            return sendInstallationDataV2(shop, v2Payload);
          },
          redirectUrl: '/settings/edit',
          replace: true,
        };

        return await formSubmitHandler(navigate, v2Config, payload, showMessage);
      }
      throw new Error('Location state undefined or missing fields'); // TODO: consider show banner instead
    },
  });

  const handleMonthChange = (month: number, year: number): void => {
    setDate({ month, year });
  };

  const handleResourcesChange = (selected: string[], name: string): void => {
    fields?.scopes?.onChange(selected as (BackfillScope | 'hidden')[]);
  };

  const handleTimeFromChange = (date: Range): void => {
    setSelectedDates(date);
  };

  return (
    <Page title="Data Backfill" narrowWidth>
      <div className={styles.bannerContainer}>{bannerMarkup}</div>

      <Form noValidate preventDefault onSubmit={submit}>
        <Layout>
          <Layout.Section>
            <p className={styles.backfillTip}>Please enter a date from which to backfill your Shopify data. This will then kickoff a series of track events and user update calls of your Shopify users and events from the specified date. You have the option down below to choose Users and/or Order data as the information that will be synced over to your Iterable project.</p>
            <p className={styles.ordersTip}>* Only the last 60 days' worth of orders from a store are accessible</p>

            <FormLayout>
              <p className={styles.datePickerLabel}>Import from</p>

              <DatePicker
                month={month}
                year={year}
                onChange={handleTimeFromChange}
                onMonthChange={handleMonthChange}
                selected={fields.timeFrom.value}
                disableDatesAfter={new Date()}
              />

              <br />

              <ChoiceList
                name="scopes"
                allowMultiple
                title="Resources"
                choices={formConfig.scopeChoices}
                selected={fields.scopes.value}
                onChange={handleResourcesChange}
              />
            </FormLayout>

            <br />

            <div className={styles.nextButtonContainer }>
              <Button submit loading={submitting}>Next</Button>
            </div>
          </Layout.Section>
        </Layout>
      </Form>
    </Page>
  );
};


export default DataBackfill;
